import { Pastille } from '../ui/Primitives';
import type { BlocEcfDossier, EvaluationDossierEcf } from '../../lib/useEcf';
import { jourLisible } from './format';

/**
 * Évaluations d'un bloc, de la plus ancienne à la plus récente : date,
 * résultat, observation de l'évaluateur. L'équipe lit en plus qui a saisi et
 * ce que les corrections ont écrasé ; l'apprenant, non.
 */
export function EvaluationsBloc({
  evaluations,
}: {
  evaluations: EvaluationDossierEcf[];
}): JSX.Element {
  if (evaluations.length === 0) {
    return <p className="text-[11px] text-codialis-gray">Aucune ECF planifiée sur ce bloc.</p>;
  }
  return (
    <ul className="space-y-1.5">
      {evaluations.map((evaluation) => (
        <li key={evaluation.id} className="text-[11px] text-codialis-gray">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono">{jourLisible(evaluation.datePrevue)}</span>
            {evaluation.resultatLibelle ? (
              <Pastille texte={evaluation.resultatLibelle} ton={evaluation.resultatTon ?? 'neutral'} />
            ) : (
              <Pastille texte={evaluation.etatLibelle} ton={evaluation.etatTon} />
            )}
            {evaluation.saisiPar && <span>saisi par {evaluation.saisiPar}</span>}
          </div>
          {evaluation.observation && (
            <p className="mt-0.5 text-codialis-navy">« {evaluation.observation} »</p>
          )}
          {(evaluation.corrections ?? []).map((correction) => (
            <p key={correction.le} className="mt-0.5">
              Corrigé le{' '}
              {new Date(correction.le).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' })}
              {correction.par ? ` par ${correction.par}` : ''} : {correction.ancien} → {correction.nouveau}{' '}
              ({correction.motif})
            </p>
          ))}
        </li>
      ))}
    </ul>
  );
}

/** Liste des blocs d'un dossier, avec leur état et leurs évaluations. */
export function BlocsEcf({ blocs }: { blocs: BlocEcfDossier[] }): JSX.Element {
  return (
    <ul className="divide-y divide-gray-100">
      {blocs.map((bloc) => (
        <li key={bloc.id} className="space-y-2 py-3 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-codialis-navy">{bloc.libelle}</div>
              {bloc.horsProgramme && (
                <div className="text-[11px] text-codialis-gray">
                  Bloc évalué qui n’est plus au programme de la formation
                </div>
              )}
            </div>
            <Pastille texte={bloc.etatLibelle} ton={bloc.etatTon} />
          </div>
          <EvaluationsBloc evaluations={bloc.evaluations} />
        </li>
      ))}
    </ul>
  );
}
